import {
  useFindAllCompaniesQuery,
  useFindAlldepartmentsQuery,
  useFindAllInventoryHardwareQuery,
  useFindAllOrdersQuery,
  useFindOneHardwareQuery,
  useGetAllTypesQuery,
  useRemoveHardwareMutation,
} from '../../api/api-hooks';
import { HardwareItemForm } from './hardwareTypes';
import { Grid, MenuItem, Select, TextField, Typography } from '@mui/material';
import { Autocomplete } from '@mui/material';
import ItemOptionsBar from '../../components/ItemOptionsBar';
import {
  convertStringTrueFalseToBoolean,
  nullStringCheck,
} from '../../utils/stringManipulation';
import { Item } from '../../utils/sharedTypes';
import {
  getISOStringOneYearFromToday,
  truncateISOString,
} from '../../utils/dateTime';
import React, { useState } from 'react';
import SoftwareSoldCreatorWithDefaults from '../software-sold/SoftwareSoldCreatorWithDefaults';
import useHardwareItemUpdater from './useHardwareItemUpdater';
import AddItemDialogWithSecondaryDialog from '../../components/AddItemDialogWithSecondaryDialog';
import { hardwareHandlerMiddleware, hardwareValidator } from './hardwareUtils';
import AnalyzerTypeManagementButton from '../analyzer-types/AnalyzerTypeManagementButton';
import useHardwareItemCreator from './useHardwareItemCreator';
import { Department, InventoryHardware } from '../../api/api-generated';

interface Props {
  selectedHardware: Item[];
  setSelectedHardware: React.Dispatch<React.SetStateAction<Item[]>>;
  editModalOpenFlag: boolean;
  setEditModalOpenFlag: React.Dispatch<React.SetStateAction<boolean>>;
}

const addFormInitialState: HardwareItemForm = {
  hardwareType: null,
  companies: [],
  firmware: undefined,
  notes: undefined,
  serial: null,
  shipper: undefined,
  warrantyStartDate: truncateISOString(new Date().toISOString()),
  warrantyEndDate: truncateISOString(getISOStringOneYearFromToday()),
  trackingNumber: undefined,
  order: null,
  lptSold: undefined,
  manufacturer: undefined,
  inventoryHardware: null,
  department: null,
};

const HardwareOptionsBar = ({
  selectedHardware,
  setSelectedHardware,
  editModalOpenFlag,
  setEditModalOpenFlag,
}: Props) => {
  const [deleteHardware] = useRemoveHardwareMutation();
  const { data: analyzerTypes } = useGetAllTypesQuery({});
  const hardwareCreator = useHardwareItemCreator();
  const hardwareUpdater = useHardwareItemUpdater();
  const [softwareDialogOpen, setSoftwareDialogOpen] = useState<boolean>(false);
  const { data: companies } = useFindAllCompaniesQuery({});
  const { data: orders } = useFindAllOrdersQuery({});
  const { data: inventoryHardware } = useFindAllInventoryHardwareQuery({});
  const { data: departments } = useFindAlldepartmentsQuery({});
  if (
    analyzerTypes === undefined ||
    companies === undefined ||
    orders === undefined ||
    inventoryHardware === undefined ||
    departments === undefined
  )
    return null;
  const filteredOrders = orders.filter((order) => !order.completed);
  return (
    <>
      <ItemOptionsBar
        selectedItems={selectedHardware}
        setSelectedItems={setSelectedHardware}
        handlerMiddleware={hardwareHandlerMiddleware}
        itemValidator={hardwareValidator}
        formInstructions='Serial #, Hardware Type, Company, and Firmware are required.'
        editModalOpen={editModalOpenFlag}
        setEditModalOpen={setEditModalOpenFlag}
        addFormInitialState={addFormInitialState}
        itemName='Hardware'
        deleteNotes='Deleting hardware will delete associated RMAs. Please use caution.'
        addDialogCreator={(
          addModalOpen: boolean,
          setAddModalOpen: React.Dispatch<React.SetStateAction<boolean>>,
          childrenCreator: Function
        ) => (
          <AddItemDialogWithSecondaryDialog
            addModalOpen={addModalOpen}
            setAddModalOpen={setAddModalOpen}
            secondaryAddModalOpen={softwareDialogOpen}
            setSecondaryAddModalOpen={setSoftwareDialogOpen}
            secondaryItemName='Software'
            addFormInitialState={addFormInitialState}
            itemCreator={hardwareCreator}
            itemValidator={hardwareValidator}
            formInstructions='Serial #, Hardware Type, Company, Firmware, and whether LPT sold the hardware are required.'
            children={childrenCreator}
            handlerMiddleware={hardwareHandlerMiddleware}
            itemName='Hardware'
          />
        )}
        childrenCreator={(addFormState: HardwareItemForm, handler: any) => (
          <Grid container spacing={3}>
            <Grid item>
              <Typography variant='body2' color='error'>
                If LPT sold the to-be-added hardware, this dialog will transfer
                the hardware from inventory. If not, this dialog will create a
                new hardware item <strong>without affecting inventory</strong>.
              </Typography>
            </Grid>
            <Grid container item xs={6} direction='column' spacing={1}>
              <Grid item container alignItems='center' spacing={1}>
                <Grid item>
                  <Typography>Did LPT sell the hardware?</Typography>
                </Grid>
                <Grid item>
                  <Select
                    value={addFormState.lptSold}
                    onChange={handler}
                    name='lptSold'
                  >
                    <MenuItem value='true'>Yes</MenuItem>
                    <MenuItem value='false'>No</MenuItem>
                  </Select>
                </Grid>
              </Grid>
              {addFormState.companies.length > 0 ? (
                addFormState.companies.map((company, idx) => (
                  <Grid item>
                    <Autocomplete
                      onChange={(event, value) => {
                        handler(event, value, `companies${idx}`);
                      }}
                      value={company}
                      options={companies}
                      getOptionLabel={(company) => company.companyName}
                      style={{ width: 300 }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label='Select Company'
                          variant='outlined'
                        />
                      )}
                    />
                  </Grid>
                ))
              ) : (
                <Grid item>
                  <Autocomplete
                    onChange={(event, value) => {
                      handler(event, value, 'companies0');
                    }}
                    value={addFormState.companies[0]}
                    options={companies}
                    getOptionLabel={(company) => company.companyName}
                    style={{ width: 300 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Company'
                        variant='outlined'
                      />
                    )}
                  />
                </Grid>
              )}
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'department');
                  }}
                  value={addFormState.department}
                  options={departments.filter((department: Department) =>
                    addFormState.companies.some(
                      (company) => company.id === department?.companyId
                    )
                  )}
                  getOptionLabel={(department) => department.departmentName}
                  style={{ width: 300 }}
                  disabled={
                    !addFormState.companies || addFormState.companies.length < 1
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Department'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                {convertStringTrueFalseToBoolean(addFormState.lptSold) ? (
                  <Autocomplete
                    onChange={(event, value: null | InventoryHardware) => {
                      handler(event, value, 'inventoryHardware');
                      const associatedType = analyzerTypes.find(
                        (type) => type.id === value?.hardwareTypeId
                      );
                      if (associatedType)
                        handler(event, associatedType, 'hardwareType');
                    }}
                    value={addFormState.inventoryHardware}
                    options={inventoryHardware}
                    getOptionLabel={(inventory) => inventory.serial}
                    style={{ width: 300, marginTop: '.5rem' }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label='Select Inventory Hardware'
                        variant='outlined'
                      />
                    )}
                  />
                ) : (
                  <TextField
                    margin='dense'
                    name='serial'
                    label='Serial #'
                    fullWidth
                    value={nullStringCheck(addFormState.serial)}
                    onChange={handler}
                  />
                )}
              </Grid>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'hardwareType');
                  }}
                  value={addFormState.hardwareType}
                  options={analyzerTypes}
                  getOptionLabel={(type) => type.typeName}
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Type'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>

              <Grid item>
                <AnalyzerTypeManagementButton />
              </Grid>

              <Grid item>
                <TextField
                  margin='dense'
                  name='firmware'
                  label='Firmware'
                  fullWidth
                  value={nullStringCheck(addFormState.firmware)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='manufacturer'
                  label='Manufacturer'
                  fullWidth
                  value={nullStringCheck(addFormState.manufacturer)}
                  onChange={handler}
                />
              </Grid>
            </Grid>
            <Grid container item xs={6} direction='column' spacing={1}>
              <Grid item>
                <Autocomplete
                  onChange={(event, value) => {
                    handler(event, value, 'order');
                  }}
                  value={addFormState.order}
                  options={filteredOrders}
                  getOptionLabel={(order) =>
                    order.purchaseOrderName
                      ? order.purchaseOrderName
                      : order.orderDate
                  }
                  style={{ width: 300 }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label='Select Order'
                      variant='outlined'
                    />
                  )}
                />
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Warranty Start: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='warrantyStartDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.warrantyStartDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <Grid container alignItems='center' spacing={1}>
                  <Grid item>
                    <Typography>Warranty End: </Typography>
                  </Grid>
                  <Grid item>
                    <TextField
                      type='date'
                      margin='dense'
                      name='warrantyEndDate'
                      value={truncateISOString(
                        nullStringCheck(addFormState.warrantyEndDate)
                      )}
                      onChange={handler}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='trackingNumber'
                  label='Tracking #'
                  fullWidth
                  value={nullStringCheck(addFormState.trackingNumber)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='shipper'
                  label='Shipper'
                  fullWidth
                  value={nullStringCheck(addFormState.shipper)}
                  onChange={handler}
                />
              </Grid>
              <Grid item>
                <TextField
                  margin='dense'
                  name='notes'
                  label='Notes'
                  multiline
                  fullWidth
                  value={nullStringCheck(addFormState.notes)}
                  onChange={handler}
                  rows={5}
                />
              </Grid>
              <SoftwareSoldCreatorWithDefaults
                company={addFormState.companies[0]}
                serial={
                  addFormState.inventoryHardware
                    ? addFormState.inventoryHardware.serial
                    : addFormState.serial
                }
                dialogOpen={softwareDialogOpen}
                setDialogOpen={setSoftwareDialogOpen}
              />
            </Grid>
          </Grid>
        )}
        itemCreator={hardwareCreator}
        itemGetter={useFindOneHardwareQuery}
        itemUpdater={hardwareUpdater}
        itemDeleter={deleteHardware}
      />
    </>
  );
};

export default HardwareOptionsBar;
